import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  Mail,
  Linkedin,
  Instagram,
  Facebook,
} from "lucide-react";

/* ✅ Types pour les liens */
type NavLink = { path: string; label: string };

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  /* ✅ Liens de navigation */
  const navLinks: NavLink[] = [
    { path: "/", label: "Accueil" },
    { path: "/about", label: "Qui sommes-nous ?" },
    { path: "/expertise", label: "Nos expertises" },
    { path: "/csr", label: "RSE & Engagements" },
    { path: "/playbook", label: "Playbook QA" },
  ];

  const contactInfo = {
    phone: "+33 (0)1 86 04 61 82",
    email: "contact@neoqualis.com",
  };

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: "https://linkedin.com/company/neoqualis/",
      color: "hover:text-blue-500",
    },
    {
      name: "Instagram",
      icon: Instagram,
      url: "https://www.instagram.com/neoqualis/",
      color: "hover:text-pink-500",
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: "https://www.facebook.com/profile.php?id=61576358416309#",
      color: "hover:text-blue-600",
    },
  ];

  return (
    <>
      <header className="fixed w-full top-0 z-50 bg-white shadow-md">
        <div className="w-full bg-gray-900 text-white py-1.5 text-xs sm:text-sm">
          <div className="container flex items-center justify-between">
            <div className="flex items-center space-x-4 sm:space-x-6">
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center space-x-1.5 hover:text-primary transition-colors whitespace-nowrap"
              >
                <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{contactInfo.phone}</span>
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center space-x-1.5 hover:text-primary transition-colors whitespace-nowrap"
              >
                <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{contactInfo.email}</span>
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-4 sm:space-x-6">
              <span className="text-xs whitespace-nowrap">
                ESN française • Testing & Business Analysis • France
              </span>
              <div className="flex items-center space-x-3">
                {socialLinks.map(({ name, icon: Icon, url, color }) => (
                  <a
                    key={name}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`transition-colors ${color}`}
                    aria-label={name}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="container py-2 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center transition-transform hover:scale-105 mr-6"
          >
            <img
              src="/neoqualis_logo.png" 
              alt="Neoqualis"
              className="h-10 w-auto transition-opacity hover:opacity-80"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition whitespace-nowrap ${
                  location.pathname === item.path
                    ? "text-primary bg-primary/5"
                    : "text-gray-700 hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-2">
            <Link
              to="/audit"
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition whitespace-nowrap"
            >
              Audit QA Gratuit
            </Link>
            <Link
              to="/contact"
              className="px-3 py-1.5 text-sm font-medium rounded-full bg-primary text-white shadow hover:shadow-lg transition whitespace-nowrap"
            >
              Nous contacter
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg transition-colors hover:bg-gray-100 focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-gray-100">
            <nav className="container py-4">
              {navLinks.map((item) => (
                <div
                  key={item.path}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <Link
                    to={item.path}
                    className={`block px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      location.pathname === item.path
                        ? "text-primary bg-primary/5"
                        : "text-gray-700 hover:text-primary hover:bg-gray-50"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}

              {/* Mobile CTA Buttons */}
              <div className="px-4 pt-4 space-y-3">
                <Link
                  to="/audit"
                  className="block w-full text-center px-4 py-2 border border-primary text-primary rounded-md font-medium hover:bg-primary hover:text-white transition whitespace-nowrap"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Audit QA Gratuit
                </Link>
                <Link
                  to="/contact"
                  className="block w-full text-center px-4 py-2 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition whitespace-nowrap"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Nous contacter
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
